import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMissingHospitalReportPlacements } from '../services/api';
import LoadingSpinner from './LoadingSpinner';
import '../styles/PlacementStatus.css';

const HIROSAKI_HOSPITAL_NAME = '弘前大学医学部附属病院';

function PlacementStatus() {
  const [placements, setPlacements] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [termFilter, setTermFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError('');
      try {
        const data = await getMissingHospitalReportPlacements();
        setPlacements(data);
      } catch (err) {
        console.error('Failed to fetch missing hospital reports:', err);
        setError('申し送り未登録リストの取得に失敗しました。時間をおいて再度お試しください。');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredPlacements = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    return placements.filter((item) => {
      const matchesKeyword =
        !keyword ||
        item.hospital_name.toLowerCase().includes(keyword) ||
        item.department_name.toLowerCase().includes(keyword);
      const matchesTerm = termFilter === 'all' ? true : item.term_group === termFilter;
      return matchesKeyword && matchesTerm;
    });
  }, [placements, searchKeyword, termFilter]);

  const summary = useMemo(() => {
    const totalAssignments = placements.reduce(
      (sum, item) => sum + item.assignment_count,
      0
    );
    const hirosakiRows = placements.filter(
      (item) => item.hospital_name === HIROSAKI_HOSPITAL_NAME
    ).length;
    return {
      totalRows: placements.length,
      totalAssignments,
      hirosakiRows,
    };
  }, [placements]);

  return (
    <div className="placement-status-container">
      <div className="page-header">
        <h1>申し送り記載担当確認</h1>
        <Link to="/dashboard" className="back-link">
          ダッシュボードに戻る
        </Link>
      </div>

      <section className="instruction-container">
        <div className="instruction-box">
          <p className="instruction-text">
            <span className="highlight-mark">ルール</span>
            {HIROSAKI_HOSPITAL_NAME}は
            <strong> 1～4ターム</strong> と <strong>5～7ターム</strong> の両方でそれぞれ最低1件、
            その他の病院は <strong>1～7タームで1件以上</strong> の申し送りが必要です。
          </p>
          <p className="instruction-text">
            この一覧には「配属があるのに申し送りが未登録」の組み合わせのみが表示されます。
          </p>
        </div>
      </section>

      <section className="status-summary">
        <div className="summary-card">
          <span className="summary-label">未対応配属先</span>
          <span className="summary-value">{summary.totalRows}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">該当学生数</span>
          <span className="summary-value">{summary.totalAssignments}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">弘前大学附属</span>
          <span className="summary-value">{summary.hirosakiRows}</span>
        </div>
      </section>

      <section className="filter-panel">
        <input
          type="text"
          placeholder="病院名・診療科で検索"
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
        />
        <select value={termFilter} onChange={(e) => setTermFilter(e.target.value)}>
          <option value="all">すべてのターム要件</option>
          <option value="1-7ターム">1-7ターム (通常)</option>
          <option value="1-4ターム">1-4ターム (弘大)</option>
          <option value="5-7ターム">5-7ターム (弘大)</option>
        </select>
      </section>

      <section className="table-section">
        {isLoading ? (
          <div className="status-message"><LoadingSpinner text="読み込み中です..." /></div>
        ) : error ? (
          <div className="status-message error">{error}</div>
        ) : filteredPlacements.length === 0 ? (
          <div className="status-message success">
            申し送りが未登録の配属先はありません。
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="placement-table">
              <thead>
                <tr>
                  <th>病院</th>
                  <th>診療科</th>
                  <th>必要ターム</th>
                  <th>配属ターム</th>
                  <th>申し送り状況</th>
                  <th>対象人数</th>
                  <th>対象者名</th>
                  <th>記載</th>
                </tr>
              </thead>
              <tbody>
                {filteredPlacements.map((item) => {
                  const assignedTermChips = item.assigned_terms.map((term) => (
                    <span key={`assign-${item.institution_id}-${item.department_id}-${term}`} className="term-chip">
                      ターム{term}
                    </span>
                  ));
                  const reportTermChips = item.existing_report_terms.length
                    ? item.existing_report_terms.map((term) => (
                        <span
                          key={`report-${item.institution_id}-${item.department_id}-${term}`}
                          className="term-chip report-chip"
                        >
                          ターム{term}
                        </span>
                      ))
                    : [
                        <span key="no-report" className="term-chip missing-chip">
                          未登録
                        </span>,
                      ];
                  return (
                    <tr
                      key={`${item.institution_id}-${item.department_id}-${item.term_group}`}
                    >
                      <td data-label="病院">
                        <div className="cell-content">{item.hospital_name}</div>
                      </td>
                      <td data-label="診療科">
                        <div className="cell-content">{item.department_name}</div>
                      </td>
                      <td data-label="必要ターム">
                        <div className="cell-content">{item.term_group}</div>
                      </td>
                      <td data-label="配属ターム">
                        <div className="term-chip-list">{assignedTermChips}</div>
                      </td>
                      <td data-label="申し送り状況" className="report-status">
                        <div className="term-chip-list">{reportTermChips}</div>
                      </td>
                      <td data-label="対象人数">
                        <div className="cell-content">
                          <span className="count-badge">{item.assignment_count}</span>
                          <span className="count-caption">名</span>
                        </div>
                      </td>
                      <td data-label="対象者名">
                        <div className="cell-content">
                          {item.target_student_names && item.target_student_names.length > 0 ? (
                            <div className="student-name-list">
                              {item.target_student_names.map((name, idx) => (
                                <span key={idx} className="student-name-item">
                                  {name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="no-data">-</span>
                          )}
                        </div>
                      </td>
                      <td data-label="申し送り">
                        <div className="cell-content">
                          <Link
                            to="/hospital-report"
                            className="action-link"
                            state={{
                              prefill: {
                                hospitalName: item.hospital_name,
                                departmentName: item.department_name,
                                termGroup: item.term_group,
                              },
                            }}
                          >
                            申し送り記入へ
                          </Link>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="help-section">
        <h3>次のアクション</h3>
        <ul>
          <li>該当する配属先の担当者に連絡し、申し送りの記入を依頼してください。</li>
          <li>入力済みタームがある場合は、残りのタームで担当者が決まっているか確認してください。</li>
          <li>
            新しく申し送りを登録する場合は「申し送り作成」ページから該当タームを選択して入力できます。
          </li>
        </ul>
      </section>
    </div>
  );
}

export default PlacementStatus;
